import { Store, EventBus, DataRouter } from "@xbs/lib-state";
import { isSameId, uid, isDefined, findIndex, isNull, getCloneObject, isObject, treeToFlatten, } from "./helpers";
import { checkIntegrity } from "./package/package";
import { defaultDragConfig, defaultTaskShape } from "./data/constants";
import { getMatchChar } from "./common/regexp";
import { getFilteredChildren, treeFilter } from "./common/filter";
import { normalizeData, normalizeTreeItem } from "./common/normalize";
import { getAvailableUsers, getUsersMap } from "./common/user";
import { closeAllElements } from "./actions/common";
import addTask from "./handlers/task/add";
import updateTask from "./handlers/task/update";
import moveTask from "./handlers/task/move";
import copyTask from "./handlers/task/copy";
import pasteTask from "./handlers/task/paste";
import deleteTask from "./handlers/task/delete";
import expandTask from "./handlers/task/expand";
import collapseTask from "./handlers/task/collapse";
import checkTask from "./handlers/task/check";
import uncheckTask from "./handlers/task/uncheck";
import indentTask from "./handlers/task/indent";
import unindentTask from "./handlers/task/unindent";
import showCompleted from "./handlers/task/showCompleted";
import hideCompleted from "./handlers/task/hideCompleted";
import selectTask from "./handlers/selection/select";
import unselectTask from "./handlers/selection/unselect";
import addProject from "./handlers/project/add";
import updateProject from "./handlers/project/update";
import setProject from "./handlers/project/set";
import deleteProject from "./handlers/project/delete";
import openMenu from "./handlers/menu/open";
import closeMenu from "./handlers/menu/close";
import clickItemMenu from "./handlers/menu/click";
import assignUser from "./handlers/user/assign";
import unassignUser from "./handlers/user/unassign";
import openInlineEditor from "./handlers/editor/open";
import closeInlineEditor from "./handlers/editor/close";
import editingItem from "./handlers/editor/editing";
import keypressOn from "./handlers/keyNavigation";
import setFilter from "./handlers/filter";
import setSort from "./handlers/sort";
import setStateSearch from "./handlers/components/search";
import setStateDueDate from "./handlers/components/duedate";
import setStateCombo from "./handlers/components/combo";
import dragStart from "./handlers/dnd/start";
import dragEnd from "./handlers/dnd/end";
import drag from "./handlers/dnd/drag";
import { sortTasks } from "./common/sort";
import { setPriorityMap } from "./common/priority";
export class DataStore extends Store {
    constructor(writable, config) {
        super(writable);
        checkIntegrity();
        this.in = new EventBus();
        this.out = new EventBus();
        this.in.setNext(this.out.exec);
        this.config = { ...{ history: true }, ...(config || {}) };
        this._router = new DataRouter(super.setState.bind(this), [], {});
        this._initStructure();
        const handlers = {
            "add-task": addTask,
            "update-task": updateTask,
            "move-task": moveTask,
            "copy-task": copyTask,
            "paste-task": pasteTask,
            "delete-task": deleteTask,
            "clone-task": () => { },
            "expand-task": expandTask,
            "collapse-task": collapseTask,
            "check-task": checkTask,
            "uncheck-task": uncheckTask,
            "indent-task": indentTask,
            "unindent-task": unindentTask,
            "add-project": addProject,
            "update-project": updateProject,
            "set-project": setProject,
            "delete-project": deleteProject,
            "show-completed-tasks": showCompleted,
            "hide-completed-tasks": hideCompleted,
            "select-task": selectTask,
            "unselect-task": unselectTask,
            "open-menu": openMenu,
            "close-menu": closeMenu,
            "click-menu-item": clickItemMenu,
            "assign-user": assignUser,
            "unassign-user": unassignUser,
            "open-inline-editor": openInlineEditor,
            "close-inline-editor": closeInlineEditor,
            "edit-item": editingItem,
            "keypress-on-todo": keypressOn,
            "set-filter": setFilter,
            "set-sort": setSort,
            "set-state-search": setStateSearch,
            "set-state-due-date": setStateDueDate,
            "set-state-combo": setStateCombo,
            "start-drag": dragStart,
            "end-drag": dragEnd,
            drag,
        };
        this.in.on("set-filter", () => {
            const { tasks, activeProject, filter } = this.getState();
            const { treeTasks, childrenMap, filteredChildrenMap, tags } = this.getInnerState(tasks, activeProject);
            if (filter?.match)
                closeAllElements(this);
            this.setState({
                treeTasks,
                childrenMap,
                filteredChildrenMap,
                tags,
            });
        });
        this._setHandlers(handlers);
    }
    init(initialState) {
        this._router.init({
            id: null,
            selected: [],
            projects: [],
            tasks: [],
            users: [],
            tags: [],
            readonly: false,
            taskShape: defaultTaskShape,
            drag: defaultDragConfig,
            priorities: [],
            menu: null,
            // Only service
            treeTasks: [],
            copiedTasksId: [],
            defaultTags: [],
            tasksMap: null,
            usersMap: null,
            childrenMap: null,
            priorityMap: null,
            filteredChildrenMap: null,
            stateEditableItem: null,
            stateSearch: null,
            stateDueDate: null,
            stateCombo: null,
            draggableIds: [],
            dragInfo: null,
            filter: null,
            menuState: null,
            historyState: {
                undo: [],
                redo: [],
                batches: {},
            },
            ...initialState,
        });
        const { tasks, users, projects, tags, priorities } = this.getState();
        this.parse({
            tasks,
            users,
            projects,
            activeProject: initialState?.activeProject,
            tags,
        });
        setPriorityMap(this, priorities);
    }
    undo() {
        const { historyState, activeProject } = this.getState();
        const config = this._findLastChange(historyState.undo, historyState.batches, activeProject);
        if (config) {
            if (typeof config === "object") {
                config.undo();
                historyState.redo.push({
                    ev: config.ev,
                    key: config.key,
                    branchId: config.branchId,
                });
            }
            else if (typeof config === "number") {
                const batch = historyState.batches[config];
                for (let i = batch.length - 1; i >= 0; i--) {
                    batch[i].undo();
                }
                historyState.redo.push(config);
            }
        }
        this.setState({ historyState });
    }
    redo() {
        const { historyState, activeProject } = this.getState();
        const redo = [...historyState.redo];
        const config = this._findLastChange(redo, historyState.batches, activeProject);
        if (config) {
            if (typeof config === "object") {
                const { ev, key } = config;
                this.in.exec(key, ev);
                this.setState({ historyState: { ...historyState, redo } });
            }
            else if (typeof config === "number") {
                const batch = historyState.batches[config];
                delete historyState.batches[config];
                batch.forEach(action => {
                    const { ev, key } = action;
                    this.in.exec(key, ev);
                });
                this.setState({ historyState: { ...historyState, redo } });
            }
        }
    }
    _findLastChange(log, batches, branchId) {
        const history = this.config.history;
        if (typeof history === "object" && !history.projects) {
            return log.pop();
        }
        for (let i = log.length - 1; i >= 0; i--) {
            const config = log[i];
            if (typeof config === "number") {
                const batch = batches[config];
                if (batch[0].branchId === "$allBranches" ||
                    batch[0].branchId === branchId) {
                    return log.splice(i, 1)[0];
                }
            }
            else if (config.branchId === "$allBranches" ||
                config.branchId === branchId) {
                return log.splice(i, 1)[0];
            }
        }
    }
    setState(state, ctx) {
        this._router.setState(state, ctx);
    }
    getSelection({ sorted = false } = {}) {
        let selected = this.getState().selected;
        if (sorted) {
            const tasks = this.getState().tasks;
            const pull = {};
            for (const id of selected) {
                pull[tasks.findIndex(t => isSameId(t.id, id))] = id;
            }
            selected = Object.values(pull);
        }
        return selected;
    }
    eachSelected(callback, sorted = false, reversed = false) {
        const selection = this.getSelection({ sorted });
        if (reversed) {
            let counter = 0;
            for (let index = selection.length - 1; index >= 0; index--) {
                callback(selection[index], counter++);
            }
        }
        else {
            for (let index = 0; index < selection.length; index++) {
                callback(selection[index], index);
            }
        }
    }
    serialize() {
        const { tasks, projects, users, tags, activeProject, priorities } = this.getState();
        for (const task of tasks) {
            delete task.selected;
            delete task.draggable;
        }
        return {
            tasks: [...tasks],
            projects: [...projects],
            users: [...users],
            tags: [...tags],
            priorities: [...priorities],
            activeProject,
        };
    }
    parse(obj) {
        if (!isObject(obj))
            return;
        const state = this.getState();
        if (obj.tasks) {
            obj.tasks = normalizeData(obj.tasks, state.selected);
        }
        if (obj.tags) {
            const tags = (obj.tags || []).map(t => t.startsWith("#") ? t : `#${t}`);
            this.setState({ defaultTags: [...new Set(tags)] });
        }
        const tasks = obj.tasks || state.tasks || [];
        const projects = obj.projects || state.projects || [];
        const users = obj.users || state.users || [];
        const priorities = Array.isArray(obj.priorities)
            ? obj.priorities
            : state.priorities;
        let activeProject = state.activeProject;
        if ((typeof obj?.activeProject !== "undefined" &&
            projects.find(p => isSameId(p.id, obj.activeProject))) ||
            isNull(obj.activeProject)) {
            activeProject = obj.activeProject;
        }
        else if (!projects.find(p => isSameId(p.id, state.activeProject))) {
            activeProject = projects[0]?.id || null;
        }
        const { treeTasks, childrenMap, filteredChildrenMap, usersMap, tags } = this.getInnerState(tasks, activeProject);
        this.setState({
            treeTasks,
            childrenMap,
            filteredChildrenMap,
            usersMap,
            tags,
            tasks,
            projects,
            users,
            activeProject,
            priorities,
        });
        if (obj.priorities) {
            setPriorityMap(this, priorities);
        }
        this._setTasksMap(tasks);
    }
    existsTask(id) {
        return id in this.getState().tasksMap;
    }
    existsProject(id) {
        return isDefined(this.getState().projects.find(project => isSameId(project.id, id))
            ?.id);
    }
    updateTask(ev) {
        const { id, task, skipStateCalc = false } = ev;
        if (!this.existsTask(id))
            return;
        let originalTask;
        const state = this.getState();
        const tasksMap = state.tasksMap;
        const tasks = state.tasks.map(t => {
            if (isSameId(id, t.id)) {
                originalTask = { ...t };
                const newTask = (ev.task = { ...t, ...task, id: t.id });
                tasksMap[t.id] = newTask;
                return newTask;
            }
            return t;
        });
        if (skipStateCalc) {
            this.setState({ tasks, tasksMap });
            return originalTask;
        }
        const { treeTasks, childrenMap, filteredChildrenMap, usersMap, tags } = this.getInnerState(tasks, state.activeProject);
        this.setState({
            tasks,
            treeTasks,
            childrenMap,
            filteredChildrenMap,
            usersMap,
            tags,
            tasksMap,
        });
        return originalTask;
    }
    moveTask({ id, project, parent, targetId, reverse, silent = false, }) {
        if (!this.existsTask(id) ||
            isSameId(id, parent) ||
            isSameId(id, targetId)) {
            return null;
        }
        const state = this.getState();
        const task = this.getTask(id);
        const isExistsParent = this.existsTask(parent);
        const isExistsTargetId = this.existsTask(targetId);
        const update = { ...task };
        update.parent = parent ?? null;
        if (isNull(project) || this.existsProject(project)) {
            update.project = project;
        }
        else if (isExistsTargetId) {
            update.project = this.getTask(targetId).project;
        }
        else if (isExistsParent) {
            update.project = this.getTask(parent).project;
        }
        else {
            update.project = state?.activeProject;
        }
        if (silent) {
            this.updateTask({ id, task: update });
        }
        else {
            this.in.exec("update-task", {
                id,
                task: update,
                skipProvider: true,
                skipStateCalc: true,
                $meta: {
                    skipHistory: true,
                },
            });
        }
        const moveChildren = this.getChildrenIds({ id, tree: true });
        const moveChildrenLengths = moveChildren.length;
        if (typeof project !== "undefined" && this.hasChildren(id)) {
            for (let index = 0; index < moveChildrenLengths; index++) {
                const child = this.getTask(moveChildren[index]);
                if ("project" in child) {
                    if (silent) {
                        this.updateTask({
                            id: child.id,
                            task: {
                                ...this.getTask(child.id),
                                project: update.project,
                            },
                        });
                    }
                    else {
                        this.in.exec("update-task", {
                            id: child.id,
                            task: {
                                ...this.getTask(child.id),
                                project: update.project,
                            },
                            skipProvider: true,
                            skipStateCalc: true,
                            $meta: {
                                skipHistory: true,
                            },
                        });
                    }
                }
            }
        }
        const tasks = state.tasks;
        const parentIndex = tasks.findIndex(t => isSameId(t.id, id));
        const moveTasks = tasks.splice(parentIndex, moveChildrenLengths + 1);
        let index = reverse ? 0 : state.tasks.length + 1;
        if (isExistsParent && !isDefined(targetId)) {
            const parentChildren = this.getChildrenIds({
                id: parent,
                tree: true,
            });
            const parentChildrenLength = parentChildren.length;
            const target = parentChildrenLength && !reverse
                ? parentChildren.at(-1)
                : parent;
            if (isSameId(id, target)) {
                index = parentIndex;
            }
            else {
                index = tasks.findIndex(t => isSameId(t.id, target)) + 1;
            }
        }
        if (isExistsTargetId) {
            const TChildren = this.getChildrenIds({
                id: targetId,
                tree: true,
            });
            if (reverse) {
                if (isSameId(parent, targetId)) {
                    if (TChildren.length && !isSameId(id, TChildren[0])) {
                        targetId = TChildren[0];
                    }
                }
                index = tasks.findIndex(t => isSameId(t.id, targetId));
            }
            else {
                for (let index = TChildren.length - 1; index >= 0; index--) {
                    const TChildId = TChildren[index];
                    if (!isSameId(TChildId, id) &&
                        !moveChildren.some(i => isSameId(i, TChildId))) {
                        targetId = TChildId;
                        break;
                    }
                }
                index = tasks.findIndex(t => isSameId(t.id, targetId)) + 1;
            }
        }
        tasks.splice(index, 0, ...moveTasks);
        const { treeTasks, childrenMap, filteredChildrenMap, usersMap } = this.getInnerState(tasks, state.activeProject);
        this.setState({
            tasks,
            treeTasks,
            childrenMap,
            filteredChildrenMap,
            usersMap,
        });
        this._setTasksMap(tasks);
        return task;
    }
    pasteTask({ targetId, parent, project, reverse, }) {
        const { copiedTasksId } = this.getState();
        const copy = (id, parent) => {
            const task = this.getTask(id);
            const copiedTask = getCloneObject(task);
            const children = this.getChildren({ id });
            copiedTask.id = uid().toString();
            copiedTask.parent = parent;
            if ("selected" in copiedTask)
                delete copiedTask.selected;
            if ("draggable" in copiedTask)
                delete copiedTask.draggable;
            copiedTasks.push(copiedTask);
            if (children.length) {
                for (let index = 0; index < children.length; index++) {
                    copy(children[index].id, copiedTask.id);
                }
            }
            return copiedTask.id;
        };
        const copiedTasks = [];
        const copiedIDs = [];
        for (const id of copiedTasksId) {
            copiedIDs.push(copy(id, parent));
        }
        for (let index = 0; index < copiedTasks.length; index++) {
            const copiedTask = copiedTasks[index];
            if (index === 0) {
                this.in.exec("add-task", {
                    task: copiedTask,
                    id: copiedTask.id,
                    parent,
                    targetId,
                    reverse,
                    project,
                    skipProvider: true,
                    $meta: {
                        skipHistory: true,
                    },
                });
            }
            else {
                this.in.exec("add-task", {
                    task: copiedTask,
                    id: copiedTask.id,
                    parent: copiedTask.parent,
                    targetId: copiedTasks[index - 1]?.id,
                    project,
                    skipProvider: true,
                    $meta: {
                        skipHistory: true,
                    },
                });
            }
        }
        copiedTasks.length &&
            this.in.exec("clone-task", {
                parent,
                targetId,
                project,
                reverse,
                batch: copiedTasks,
            });
        return copiedIDs;
    }
    getTask(id) {
        return this.getState().tasksMap[id];
    }
    getProject(id) {
        return this.getState().projects.find(p => isSameId(p.id, id));
    }
    getChildren({ id, tree = false, filtered = false, hideCompleted = false, }) {
        if (!this.existsTask(id) && isDefined(id)) {
            return;
        }
        let treeChildren = [];
        if (isDefined(id)) {
            const children = this.getState()[filtered ? "filteredChildrenMap" : "childrenMap"][id];
            treeChildren = [...treeChildren, ...children];
            if (tree) {
                for (let index = 0; index < children.length; index++) {
                    const child = children[index];
                    if (child?.children?.length) {
                        treeChildren = [
                            ...treeChildren,
                            ...this.getChildren({
                                id: child.id,
                                tree: true,
                                filtered,
                            }),
                        ];
                    }
                }
            }
        }
        else {
            treeChildren = this.getState().treeTasks;
        }
        return hideCompleted
            ? treeChildren.filter(t => !t.checked)
            : treeChildren;
    }
    getChildrenIds({ id, tree = true, filtered = false, hideCompleted = false, }) {
        if (!this.existsTask(id) && isDefined(id))
            return;
        let ids = [];
        const data = this.getChildren({
            id,
            tree: false,
            filtered,
            hideCompleted,
        });
        for (let index = 0; index < data.length; index++) {
            const child = data[index];
            ids.push(child.id);
            if (child?.children?.length && tree) {
                ids = [
                    ...ids,
                    ...this.getChildrenIds({
                        id: child.id,
                        tree,
                        filtered,
                        hideCompleted,
                    }),
                ];
            }
        }
        return ids;
    }
    getParentIds(id) {
        if (!this.existsTask(id))
            return;
        const parents = [];
        const parentId = this.getTask(id)?.parent;
        if (this.existsTask(parentId)) {
            parents.push(parentId, ...this.getParentIds(parentId));
        }
        return parents;
    }
    getTailId(id, filtered = false, hideCompleted = false) {
        const childrenIds = this.getChildrenIds({
            id,
            filtered,
            hideCompleted,
        });
        if (childrenIds.length) {
            return childrenIds.at(-1);
        }
        return id;
    }
    hasChildren(id, filtered, hideCompleted = false) {
        const children = this.getState()[filtered ? "filteredChildrenMap" : "childrenMap"];
        return !!(hideCompleted ? children[id]?.filter(t => !t.checked) : children[id])?.length;
    }
    getTreeIndex(id, filtered = false, hideCompleted = false) {
        const task = this.getTask(id);
        return this.getChildren({
            id: task?.parent,
            filtered,
            hideCompleted,
        }).findIndex(i => isSameId(i.id, id));
    }
    getNearId({ id, dir = "next", flat = false, filtered = false, hideCompleted = false, }) {
        if (!this.existsTask(id)) {
            return;
        }
        return dir === "next"
            ? this._getNextId(id, flat, false, filtered, hideCompleted)
            : this._getPrevId(id, flat, filtered, hideCompleted);
    }
    getNearProjectId(id) {
        const projects = this.getState().projects;
        const index = projects.findIndex(p => p.id === id);
        if (index > 0) {
            return projects[index - 1].id;
        }
        return projects[index + 1]?.id || null;
    }
    getInnerState(tasks, project, sort) {
        const { users, filter, taskShape, defaultTags } = this.getState();
        const rawData = getCloneObject(tasks);
        const tags = new Set(defaultTags);
        const usersMap = getUsersMap(users);
        const map = {};
        const childrenMap = {};
        let filteredChildrenMap = {};
        let treeTasks = [];
        for (let index = 0; index < rawData.length; index++) {
            const task = rawData[index];
            if (task.assigned?.length && users?.length) {
                task.availableUsers = getAvailableUsers(usersMap, task);
            }
            map[rawData[index].id] = index;
            rawData[index].children = [];
            childrenMap[task.id] = [];
            if (task.text) {
                const matches = task.text.matchAll(getMatchChar("#"));
                for (const match of matches)
                    tags.add(match[0]);
            }
        }
        for (let index = 0; index < rawData.length; index++) {
            const task = rawData[index];
            if (task.parent && childrenMap[task.parent]) {
                rawData[map[task.parent]].children.push(task);
                childrenMap[task.parent].push(task);
            }
            else if (!isDefined(task.parent)) {
                treeTasks.push(task);
            }
        }
        treeTasks = treeTasks.filter((task) => isSameId(task.project, project));
        if (sort) {
            const firstTreeId = treeTasks.at(0)?.id;
            sortTasks(treeTasks, sort);
            const sortedTasks = treeToFlatten(treeTasks);
            const index = tasks.findIndex(t => isSameId(t.id, firstTreeId));
            tasks.splice(index, sortedTasks.length, ...sortedTasks);
            this.setState({ tasks });
        }
        if (isDefined(filter)) {
            treeTasks = treeFilter(treeTasks, filter);
            filteredChildrenMap = getFilteredChildren(treeTasks);
        }
        for (let index = 0; index < treeTasks.length; index++) {
            normalizeTreeItem(treeTasks[index], childrenMap, taskShape);
        }
        return {
            treeTasks,
            childrenMap,
            usersMap,
            filteredChildrenMap,
            tags: [...tags],
        };
    }
    _setTasksMap(tasks) {
        const tasksMap = {};
        for (const task of tasks)
            tasksMap[task.id] = task;
        this.setState({ tasksMap });
    }
    _setHandlers(handlersMap) {
        const { historyState } = this.getState();
        Object.keys(handlersMap).forEach((key) => {
            this.in.on(key, (ev) => {
                var _a, _b;
                const undo = handlersMap[key](this, ev);
                const meta = ev?.$meta;
                if (this.config.history && !!undo && !meta?.skipHistory) {
                    const activeProject = this.getState().activeProject;
                    if (meta?.batch) {
                        (_a = historyState.batches)[_b = meta.batch] ?? (_a[_b] = []);
                        historyState.batches[meta.batch].push({
                            undo,
                            key,
                            ev,
                            branchId: meta?.branchId || activeProject,
                        });
                        if (!historyState.undo.includes(meta.batch)) {
                            historyState.undo.push(meta.batch);
                        }
                    }
                    else {
                        historyState.undo.push({
                            undo,
                            key,
                            ev,
                            branchId: meta?.branchId || activeProject,
                        });
                    }
                    if (typeof this.config.history === "object" &&
                        historyState.undo.length > this.config.history.limit) {
                        historyState.undo.shift();
                    }
                    historyState.redo.forEach(config => {
                        if (typeof config === "string") {
                            delete historyState.batches[config];
                        }
                    });
                    historyState.redo = [];
                    this.setState({ historyState });
                }
            });
        });
    }
    _initStructure() {
        this.setState({
            id: null,
            selected: [],
            projects: [],
            tasks: [],
            users: [],
            tags: [],
            readonly: false,
            taskShape: defaultTaskShape,
            drag: defaultDragConfig,
            priorities: [],
            // Only service
            treeTasks: [],
            copiedTasksId: [],
            defaultTags: [],
            tasksMap: null,
            usersMap: null,
            childrenMap: null,
            priorityMap: null,
            filteredChildrenMap: null,
            stateEditableItem: null,
            stateSearch: null,
            stateDueDate: null,
            stateCombo: null,
            draggableIds: [],
            dragInfo: null,
            filter: null,
            menu: null,
            historyState: {
                undo: [],
                redo: [],
                batches: {},
            },
        });
    }
    _getNextId(id, flat = false, silent = false, filtered = false, hideCompleted = false) {
        const task = this.getTask(id);
        const hasChildren = this.hasChildren(id, filtered, hideCompleted);
        if (!flat && hasChildren && !task.collapsed && !silent) {
            return this.getChildren({ id, filtered, hideCompleted })[0]?.id;
        }
        else {
            const parentId = this.getTask(task?.parent)?.id;
            const children = this.getChildren({
                id: parentId,
                filtered,
                hideCompleted,
            });
            const index = findIndex(children, child => isSameId(child.id, id));
            const length = children.length;
            if (index + 1 < length) {
                return children[index + 1]?.id;
            }
            else {
                if (!isDefined(parentId)) {
                    return null;
                }
                return this._getNextId(parentId, flat, true, filtered, hideCompleted);
            }
        }
    }
    _getPrevId(id, flat = false, filtered = false, hideCompleted = false) {
        const index = this.getTreeIndex(id, filtered, hideCompleted);
        const parentId = this.getTask(id)?.parent;
        if (index > 0) {
            const children = this.getChildren({
                id: parentId,
                filtered,
                hideCompleted,
            });
            let current = children[index - 1];
            if (!this.hasChildren(current.id, filtered, hideCompleted) ||
                current.collapsed) {
                return current.id;
            }
            while (!flat &&
                this.hasChildren(current.id, filtered, hideCompleted) &&
                !current.collapsed) {
                const allChildren = this.getChildren({
                    id: current.id,
                    filtered,
                    hideCompleted,
                });
                current = allChildren[allChildren.length - 1];
            }
            return current.id;
        }
        else {
            if (!isDefined(parentId)) {
                return null;
            }
            return parentId;
        }
    }
}
//# sourceMappingURL=DataStore.js.map